import { FpjsProvider } from "@fingerprintjs/fingerprintjs-pro-react";
import Cookies from "js-cookie";
import moment from "moment";
import {
  FC,
  FunctionComponent,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { None, Option } from "space-monad";
import styled from "styled-components";
import { ReactComponent as LogoSVG } from "../../../assets/logo-long.svg";
import { useWindowSize } from "../../../components/hooks/window-size-hook";
import { CenteredFlex, columnFlex } from "../../../components/layout";
import { WrapWithStepperContext } from "../../../components/stepper/context";
import { useLoadUserLanguage } from "../../../i18n";
import { AppStore } from "../../../store";
import { AuthDTO } from "../../../store/auth/models/dto";
import {
  Fetchable,
  isFetchableSuccess,
} from "../../../store/common/fetchable/helpers";

import { useFilteredPagesOnAuth } from "../../../store/launch-configurations/models/default-fields";
import { LaunchConfigurationsDTO } from "../../../store/launch-configurations/models/dto";
import { Field } from "../../../store/launch-configurations/models/internal/field";
import { LaunchesDTO } from "../../../store/launches/models/dto";
import { device } from "../../../styles/media";
import { FormPrimaryButton, FormSecondaryButton } from "./components";
import { ConfigContext, useConfigState } from "./config-context";
import { HeaderContainer } from "./header";
import { useRunInitialValues } from "./initial-values";
import { KYCVerificaitonSection } from "./kyc-section";
import { LanguagePicker } from "./language-picker";
import { Page } from "./page";
import { ParticipationClosedPage } from "./pages/closed";
import { IEmailVerificationPageProps, useEmailPage } from "./pages/email";
import { useFirstPage } from "./pages/first/first";
import ResultPage from "./result-page";
import {
  FormHorizontalLinearStepper,
  FormVerticalLinearStepper,
} from "./steppers";

const LaunchLogo = styled.img`
  max-width: 75%;
  max-height: 10em;

  @media ${device.laptop} {
    max-width: 270px;
  }
`;

const Wrapper = styled.div`
  ${columnFlex};
  flex-grow: 1;
`;

const PoweredBy = styled.div`
  ${CenteredFlex};
  justify-content: flex-end;
  font-size: 0.8em;
  margin-top: 1em;
`;

const StyledLogo = styled(LogoSVG)`
  width: 8em;
  margin-left: 1.5em;
`;

/**
 * Used to debug. When testing a participation we might want to avoid going through all the steps upon each refresh
 * @returns step number
 */
const useCustomStep = () => {
  const [step, setStep] = useState<string>();

  useEffect(() => {
    const cookie = Cookies.get("custom_page");
    if (cookie) {
      setStep(cookie);
    }
  }, []);

  return step;
};

export const SingleLaunchPage: typeof SingleLaunchPageInternal = (props) => (
  <FpjsProvider
    loadOptions={{
      apiKey: "DgMQX4oqLDJf8Cek4a3C",
      endpoint: process.env.REACT_APP_FP_ENDPOINT,
    }}
  >
    <SingleLaunchPageInternal {...props} />
  </FpjsProvider>
);

export const SingleLaunchPageInternal: FunctionComponent<{
  item: LaunchConfigurationsDTO.Responses.GetSingle.T;
}> = ({ item }) => (
  <ConfigContext.Provider value={{ config: item, state: useConfigState(item) }}>
    <SingleLaunchPageInternalTwo />
  </ConfigContext.Provider>
);

export const SingleLaunchPageInternalTwo: FunctionComponent<{}> = ({ }) => {
  const { isMobile } = useWindowSize();
  const { t, i18n } = useTranslation("token-onboarding");
  const currentLanguage = i18n.language.replaceAll("-", "_");
  const { config, state } = useContext(ConfigContext);

  useLoadUserLanguage();

  // Login fetchable used to check if we need email page
  const login = useSelector<AppStore, Fetchable<AuthDTO.Responses.Login.T>>(
    (state) => state.auth.login
  );
  const moddedPages = useFilteredPagesOnAuth(config, state);

  useEffect(() => {
    if (config.configuration.launchName?.[currentLanguage]) {
      document.title = config.configuration.launchName[currentLanguage];
    }
  }, [config]);

  const paramsReady = useRunInitialValues(config, state);

  const [error, setError] = useState<Option<Error>>(None);

  const customStep = useCustomStep();

  const firstPage = useFirstPage();
  const emailPage = useEmailPage();

  // Depending on the user's authentication status, we remove unnecessary pages
  const steps = useMemo(
    () =>
      moddedPages.map(({ fields, title }, i) => {
        const content: FC<{}> = () => {
          return (
            <Page
              allFields={moddedPages.reduce<Field.Field<string>[]>(
                (p, c) => [...p, ...c.fields],
                []
              )}
              fields={fields}
              item={config}
              index={i}
              key={title[currentLanguage]}
              isLast={i === moddedPages.length - 1}
              setError={i === moddedPages.length - 1 ? setError : undefined}
            />
          );
        };

        return {
          title: title[currentLanguage],
          content,
        };
      }),
    [config, moddedPages, currentLanguage]
  );

  const stepsWithExtraPages: Array<{
    title: string;
    content: FC<IEmailVerificationPageProps & {}>;
  }> = useMemo(
    () =>
      (!isFetchableSuccess(login) &&
        config.configuration.merchantConfig.performOtp) ||
        !config.configuration.merchantConfig.shouldUseShortFlow
        ? customStep
          ? [steps[parseInt(customStep, 10)]]
          : [firstPage, emailPage, ...steps]
        : [firstPage, ...steps],
    [steps, login, emailPage, isFetchableSuccess, config, customStep]
  );

  const { css, header, between } = config.configuration;

  const isParticipationClosed = useMemo(
    () =>
      between &&
      (moment().isAfter(moment.utc(between.end)) ||
        moment().isBefore(moment.utc(between.start))),
    [between]
  );

  const shouldShowHeader = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return !params.has("showHeader") || params.get("showHeader") !== "false";
  }, []);

  return paramsReady ? (
    <WrapWithStepperContext>
      <LanguagePicker />
      <Wrapper>
        {shouldShowHeader ? (
          <HeaderContainer
            {...css.headerBackgroundColor}
            colour={css.headerTextColor}
          >
            {header.imageUrl ? <LaunchLogo src={header.imageUrl} /> : null}
            <p dangerouslySetInnerHTML={{
              __html: header.text?.[currentLanguage],
            }}></p>
          </HeaderContainer>
        ) : null}
        {config.configuration.merchantConfig.shouldUseShortFlow ? (
          <KYCVerificaitonSection />
        ) : null}
        {isParticipationClosed ? (
          <ParticipationClosedPage />
        ) : isMobile ? (
          <FormVerticalLinearStepper
            stepsProps={{ item: config }}
            steps={stepsWithExtraPages}
            finalPage={<ResultPage error={error} />}
            nextButton={<FormPrimaryButton />}
            backButton={<FormSecondaryButton />}
          />
        ) : (
          <FormHorizontalLinearStepper
            stepsProps={{ item: config }}
            steps={stepsWithExtraPages}
            finalPage={<ResultPage error={error} />}
            nextButton={<FormPrimaryButton />}
            backButton={<FormSecondaryButton />}
          />
        )}
      </Wrapper>
      <PoweredBy>
        <a href="http://www.sekuritance.com" rel="noreferrer" target="_blank">
          <div>{t("powered-by")}</div>
          <StyledLogo />
        </a>
      </PoweredBy>
    </WrapWithStepperContext>
  ) : null;
};
